import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, ArrowLeft, Cpu, FileText, Building2, MapPin, Boxes, Repeat, Plus, PowerOff, Play } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import Breadcrumbs from "@/components/Breadcrumbs";
import MetaCard from "@/components/MetaCard";
import StatusBadge from "@/components/StatusBadge";
import FileUploader from "@/components/FileUploader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { formatDate, formatDateOnly } from "@/lib/format";
import { readBack, useWithBack, segmentBackOverride } from "@/lib/nav";

const FREQ_LABELS = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  six_monthly: "6-Monthly",
  annual: "Annual",
  custom_days: "Custom (days)",
};

export default function MachineDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canScheduleWrite = hasPermission("schedule.write");
  const [searchParams] = useSearchParams();
  const backCtx = readBack(searchParams);
  const withBackHref = useWithBack();
  const [machine, setMachine] = useState(null);
  const [merged, setMerged] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: m }, { data: ml }, { data: sc }] = await Promise.all([
        api.get(`/machines/${id}`),
        api.get(`/machines/${id}/manuals`),
        api.get(`/service-schedules`, { params: { machine_id: id, active: "all", limit: 20 } }).catch(() => ({ data: { items: [] } })),
      ]);
      setMachine(m);
      setMerged(ml.items || []);
      setSchedules(sc.items || []);
    } catch (err) {
      toast.error(formatApiError(err));
      navigate("/machines", { replace: true });
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const activeSchedule = schedules.find((s) => s.active);
  const inactiveSchedules = schedules.filter((s) => !s.active);

  const toggleActive = async (schedule, active) => {
    try {
      await api.patch(`/service-schedules/${schedule.id}`, { active });
      toast.success(active ? "Schedule activated" : "Schedule paused");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deactivate = async (schedule) => {
    try {
      await api.delete(`/service-schedules/${schedule.id}`);
      toast.success("Schedule deactivated");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading || !machine) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="p-6 space-y-6" data-testid="machine-detail-page">
      <Breadcrumbs
        items={[
          { label: "Customers", to: segmentBackOverride("/customers", backCtx?.to) },
          ...(machine.customer
            ? [{
                label: machine.customer.name,
                to: segmentBackOverride(`/customers/${machine.customer.id}`, backCtx?.to),
              }]
            : []),
          ...(machine.site
            ? [
                {
                  label: "Sites",
                  to: segmentBackOverride(
                    machine.customer ? `/customers/${machine.customer.id}?tab=sites` : "/sites",
                    backCtx?.to,
                  ),
                },
                {
                  label: machine.site.name,
                  to: segmentBackOverride(`/sites/${machine.site.id}`, backCtx?.to),
                },
                {
                  label: "Machines",
                  to: segmentBackOverride(`/sites/${machine.site.id}?tab=machines`, backCtx?.to),
                },
              ]
            : [{ label: "Machines", to: segmentBackOverride("/machines", backCtx?.to) }]),
          { label: `${machine.make || ""} ${machine.model || ""}`.trim() || machine.serial || "Machine" },
        ]}
      />
      <PageHeader
        testId="machine-detail-header"
        title={`${machine.make} ${machine.model}`}
        subtitle={
          <span className="flex items-center gap-2 flex-wrap">
            {machine.active ? <StatusBadge tone="success">Active</StatusBadge> : <StatusBadge tone="muted">Inactive</StatusBadge>}
            <span className="font-mono text-xs text-muted-foreground">Serial {machine.serial}</span>
          </span>
        }
        actions={<>
          <Button asChild variant="outline" size="sm" data-testid="machine-detail-back"><Link to={backCtx?.to || "/machines"}><ArrowLeft className="h-4 w-4" /> Back</Link></Button>
          {hasPermission("machine.write") && (<Button asChild size="sm" data-testid="machine-detail-edit"><Link to={`/machines/${machine.id}/edit`}><Pencil className="h-4 w-4" /> Edit</Link></Button>)}
        </>}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="rounded-md">
            <CardHeader><CardTitle className="text-sm font-medium flex items-center gap-2"><Cpu className="h-4 w-4 text-primary" /> Specifications</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <Field label="Make" value={machine.make} />
              <Field label="Model" value={machine.model} />
              <Field label="Serial" value={<span className="font-mono">{machine.serial || "—"}</span>} />
              <Field label="Install date" value={formatDateOnly(machine.install_date)} />
            </CardContent>
          </Card>

          {machine.notes && (
            <Card className="rounded-md">
              <CardHeader><CardTitle className="text-sm font-medium">Notes</CardTitle></CardHeader>
              <CardContent className="text-sm leading-relaxed whitespace-pre-wrap">{machine.notes}</CardContent>
            </Card>
          )}

          <Card className="rounded-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm font-medium flex items-center gap-2"><Repeat className="h-4 w-4 text-primary" /> Service Schedule</CardTitle>
              {canScheduleWrite && !activeSchedule && (
                <Button asChild size="sm" variant="outline" data-testid="machine-detail-add-schedule">
                  <Link to={`/scheduling/schedules/new?machine_id=${machine.id}&back=${encodeURIComponent(`/machines/${machine.id}`)}`}><Plus className="h-3.5 w-3.5" /> Add schedule</Link>
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {activeSchedule ? (
                <div className="space-y-3" data-testid="machine-detail-active-schedule">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                    <Field label="Frequency" value={
                      <StatusBadge tone="info">
                        {activeSchedule.frequency === "custom_days"
                          ? `Every ${activeSchedule.custom_days}d`
                          : (FREQ_LABELS[activeSchedule.frequency] || activeSchedule.frequency)}
                      </StatusBadge>
                    } />
                    <Field label="Anchor" value={formatDateOnly(activeSchedule.anchor_due_date)} />
                    <Field label="Next due" value={machine.next_due_at ? formatDateOnly(machine.next_due_at) : "—"} />
                    <Field label="Active" value={
                      <Switch
                        checked
                        onCheckedChange={(v) => toggleActive(activeSchedule, v)}
                        disabled={!canScheduleWrite}
                        data-testid={`machine-detail-schedule-active-${activeSchedule.id}`}
                      />
                    } />
                  </div>
                  {activeSchedule.notes && (
                    <div className="text-xs text-muted-foreground whitespace-pre-wrap border-l-2 border-border pl-3">
                      {activeSchedule.notes}
                    </div>
                  )}
                  {canScheduleWrite && (
                    <div className="flex items-center gap-2">
                      <Button asChild size="sm" variant="outline" data-testid={`machine-detail-schedule-edit-${activeSchedule.id}`}>
                        <Link to={`/scheduling/schedules/${activeSchedule.id}/edit`}><Pencil className="h-3.5 w-3.5" /> Edit</Link>
                      </Button>
                      <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => deactivate(activeSchedule)} data-testid={`machine-detail-schedule-deactivate-${activeSchedule.id}`}>
                        <PowerOff className="h-3.5 w-3.5" /> Deactivate
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-xs text-muted-foreground italic" data-testid="machine-detail-no-schedule">
                  No active service schedule. Machines without a schedule won&apos;t appear on the Due Soon list.
                </div>
              )}

              {inactiveSchedules.length > 0 && (
                <div className="mt-4 pt-4 border-t border-border space-y-2">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Previously paused</div>
                  {inactiveSchedules.map((s) => (
                    <div key={s.id} className="flex items-center justify-between text-xs" data-testid={`machine-detail-schedule-inactive-${s.id}`}>
                      <div>
                        {FREQ_LABELS[s.frequency] || s.frequency}
                        {s.frequency === "custom_days" && ` (${s.custom_days}d)`}
                        <span className="text-muted-foreground"> · anchor {s.anchor_due_date}</span>
                      </div>
                      {canScheduleWrite && (
                        <Button size="sm" variant="ghost" onClick={() => toggleActive(s, true)} data-testid={`machine-detail-schedule-reactivate-${s.id}`}>
                          <Play className="h-3.5 w-3.5" /> Reactivate
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="rounded-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm font-medium flex items-center gap-2"><FileText className="h-4 w-4 text-primary" /> All manuals (type + machine)</CardTitle>
            </CardHeader>
            <CardContent>
              <FileUploader
                files={merged}
                readOnly
                testIdPrefix="machine-detail-manuals"
                emptyLabel="No manuals available for this machine."
              />
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <MetaCard
            title="Location"
            fields={[
              {
                label: "Customer",
                value: machine.customer ? (
                  <Link to={withBackHref(`/customers/${machine.customer.id}`)} className="inline-flex items-center gap-1 hover:text-primary" data-testid="machine-meta-customer">
                    <Building2 className="h-3 w-3" /> {machine.customer.name}
                  </Link>
                ) : "—",
              },
              {
                label: "Site",
                value: machine.site ? (
                  <Link to={withBackHref(`/sites/${machine.site.id}`)} className="inline-flex items-center gap-1 hover:text-primary" data-testid="machine-meta-site">
                    <MapPin className="h-3 w-3" /> {machine.site.name}
                  </Link>
                ) : "—",
              },
              {
                label: "Type",
                value: machine.machine_type ? (
                  <Link to={withBackHref(`/machine-types/${machine.machine_type.id}`)} className="inline-flex items-center gap-1 hover:text-primary" data-testid="machine-meta-type">
                    <Boxes className="h-3 w-3" /> {machine.machine_type.name}
                  </Link>
                ) : "—",
              },
            ]}
          />
          <MetaCard
            title="Metadata"
            fields={[
              { label: "Status", value: machine.active ? "Active" : "Inactive" },
              { label: "Next due", value: machine.next_due_at ? formatDateOnly(machine.next_due_at) : "—", testId: "machine-meta-next-due" },
              { label: "Manuals", value: merged.length, testId: "machine-meta-manuals" },
              { label: "Created", value: formatDate(machine.created_at) },
              { label: "Updated", value: formatDate(machine.updated_at) },
            ]}
          />
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-sm mt-0.5">{value || "—"}</div>
    </div>
  );
}
